/*
INTELLECTUAL PROPERTY NOTICE
© Orbit Tickets Ltd. All rights reserved.
This file contains Custom / Included Intellectual Property created for and owned by the Customer as part of an application developed by Kaleida within the Platform.
Custom / Included Intellectual Property may be used, reproduced, adapted, and incorporated into any application, system, or product developed by or on behalf of the Customer, whether or not such application forms part of the Platform, subject always to the terms of the agreement and provided that such use does not include or rely upon any Protected Intellectual Property of Kaleida Ltd.
For the avoidance of doubt, the inclusion of any application within the Platform does not restrict the Customer's right to use Custom / Included Intellectual Property outside of the Platform.
*/

// IP-NOTICE: CUSTOM
import { Component, OnInit } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AsideService } from 'app/layout/components/aside-container/aside.service';
import { CreateUpdateBaseComponent } from 'app/shared/components/create-update-base/create-update-base.component';
import { IMAGE_UPLOAD_CONFIGURATION } from 'app/shared/components/upload/upload.models';
import { ReactiveFormHelperService } from 'app/shared/helpers/reactive-form-helper.service';
import { ApiResponseSummary } from 'app/shared/models/api-info.model';
import { RegexValidationStrings } from 'app/shared/validation/reg-ex-validation-constants';

import { HttpClient } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';
import { TicketShopDesignFormModel } from 'app/modules/design/ticket-shop-design/ticket-shop-design-management/models/ticket-shop-design-form.model';
import { TicketShopDesignListService } from 'app/modules/design/ticket-shop-design/ticket-shop-design-management/services/ticket-shop-design-list.service';
import { EventDetailService } from 'app/modules/event/event-detail/services/event-detail.service';
import { SocialMediaFormListComponent } from 'app/shared/components/social-media-form-list/social-media-form-list.component';
import { logValidationErrorsForDevAndTest } from 'app/shared/helpers/form-validation-helpers';
import { DesignBaseFontSize } from 'app/shared/models/design-base-font-size';
import { DesignFont } from 'app/shared/models/design-font';
import { DesignHomePageLayout } from 'app/shared/models/design-home-page-layout';
import { DesignRoundedCornerOption } from 'app/shared/models/design-rounded-corner-option';
import { ImageUploadErrorType } from 'app/shared/models/image-upload-error-type';
import { ConfirmationDialogService } from 'app/shared/services/confirmation-dialog.service';
import { Subscription } from 'rxjs';

@Component({
	selector: 'ticket-shop-design-form',
	templateUrl: './ticket-shop-design-form.component.html',
	styleUrls: ['./ticket-shop-design-form.component.scss'],
	standalone: false,
})
export class TicketShopDesignFormComponent extends CreateUpdateBaseComponent<TicketShopDesignFormModel> implements OnInit {
	readonly imageUploadConfiguration = IMAGE_UPLOAD_CONFIGURATION;
	backgroundImageValidationMessage: string;
	logoValidationMessage: string;
	backgroundImageIsValid: boolean;
	logoIsValid: boolean;
	socialMedialListInvalid: boolean;

	roundedCornersLookupData: any[];
	baseFontSizeLookupData: any[];
	headingFontLookupData: any[];
	standardTextFontLookupData: any[];
	buttonFontLookupData: any[];
	buttonRoundedCornersLookupData: any[];
	homepageLayoutLookupData: any[];

	useDefaultBackgroundImageChangedSubscription: Subscription;
	useDefaultLogoChangedSubscription: Subscription;
	socialMediaArrayChangedSubscription: Subscription;

	constructor(
		protected fb: FormBuilder,
		private route: ActivatedRoute,
		private router: Router,
		private service: TicketShopDesignListService,
		protected asideService: AsideService,
		protected formHelperService: ReactiveFormHelperService,
		protected confirmationDialogService: ConfirmationDialogService,
		private http: HttpClient,
		private eventDetailService: EventDetailService,
	) {
		super(fb, asideService, formHelperService, confirmationDialogService);
	}
	//#region shortcuts to controls for readability
	get name(): AbstractControl {
		return this.dataForm.controls['name'];
	}
	get backgroundColour(): AbstractControl {
		return this.dataForm.controls['backgroundColour'];
	}
	get useDefaultBackgroundImage(): AbstractControl {
		return this.dataForm.controls['useDefaultBackgroundImage'];
	}
	get backgroundImage(): AbstractControl {
		return this.dataForm.controls['backgroundImage'];
	}
	get useDefaultLogo(): AbstractControl {
		return this.dataForm.controls['useDefaultLogo'];
	}
	get logo(): AbstractControl {
		return this.dataForm.controls['logo'];
	}
	get showSocialMediaLinks(): AbstractControl {
		return this.dataForm.controls['showSocialMediaLinks'];
	}
	get socialMediaArray(): FormArray {
		return this.dataForm.controls['socialMediaList'] as FormArray;
	}
	get headingBackgroundColour(): AbstractControl {
		return this.dataForm.controls['headingBackgroundColour'];
	}
	get headingTextColour(): AbstractControl {
		return this.dataForm.controls['headingTextColour'];
	}
	get borderColour(): AbstractControl {
		return this.dataForm.controls['borderColour'];
	}
	get contentAreaBackgroundColour(): AbstractControl {
		return this.dataForm.controls['contentAreaBackgroundColour'];
	}
	get roundedCorners(): AbstractControl {
		return this.dataForm.controls['roundedCorners'];
	}
	get controlShadow(): AbstractControl {
		return this.dataForm.controls['controlShadow'];
	}
	get baseFontSize(): AbstractControl {
		return this.dataForm.controls['baseFontSize'];
	}
	get headingFont(): AbstractControl {
		return this.dataForm.controls['headingFont'];
	}
	get headingFontUppercase(): AbstractControl {
		return this.dataForm.controls['headingFontUppercase'];
	}
	get standardTextFont(): AbstractControl {
		return this.dataForm.controls['standardTextFont'];
	}
	get standardTextFontColour(): AbstractControl {
		return this.dataForm.controls['standardTextFontColour'];
	}
	get importantTextFontColour(): AbstractControl {
		return this.dataForm.controls['importantTextFontColour'];
	}
	get linkColour(): AbstractControl {
		return this.dataForm.controls['linkColour'];
	}
	get linkUnderline(): AbstractControl {
		return this.dataForm.controls['linkUnderline'];
	}
	get hoverColour(): AbstractControl {
		return this.dataForm.controls['hoverColour'];
	}
	get hoverUnderline(): AbstractControl {
		return this.dataForm.controls['hoverUnderline'];
	}
	get buttonFont(): AbstractControl {
		return this.dataForm.controls['buttonFont'];
	}
	get buttonFontUppercase(): AbstractControl {
		return this.dataForm.controls['buttonFontUppercase'];
	}
	get buttonRoundedCorners(): AbstractControl {
		return this.dataForm.controls['buttonRoundedCorners'];
	}
	get primaryButtonBackgroundColour(): AbstractControl {
		return this.dataForm.controls['primaryButtonBackgroundColour'];
	}
	get primaryButtonTextColour(): AbstractControl {
		return this.dataForm.controls['primaryButtonTextColour'];
	}
	get primaryButtonBorderColour(): AbstractControl {
		return this.dataForm.controls['primaryButtonBorderColour'];
	}
	get secondaryButtonBackgroundColour(): AbstractControl {
		return this.dataForm.controls['secondaryButtonBackgroundColour'];
	}
	get secondaryButtonTextColour(): AbstractControl {
		return this.dataForm.controls['secondaryButtonTextColour'];
	}
	get secondaryButtonBorderColour(): AbstractControl {
		return this.dataForm.controls['secondaryButtonBorderColour'];
	}
	get homepageLayout(): AbstractControl {
		return this.dataForm.controls['homepageLayout'];
	}
	get enableSearch(): AbstractControl {
		return this.dataForm.controls['enableSearch'];
	}
	get termsAndConditionsUrl(): AbstractControl {
		return this.dataForm.controls['termsAndConditionsUrl'];
	}
	get contactUsUrl(): AbstractControl {
		return this.dataForm.controls['contactUsUrl'];
	}

	get showSocialMediaList(): boolean {
		return this.showSocialMediaLinks?.value === true;
	}

	//#endregion

	//#region subscribe to value changes methods
	private onDefaultBackgroundImageChanged(useDefaultBackgroundImage: boolean) {
		if (useDefaultBackgroundImage) {
			this.backgroundImage.clearValidators();
		} else {
			this.backgroundImage.setValidators([Validators.required]);
		}
		this.backgroundImage.updateValueAndValidity();
	}
	private onDefaultLogoChanged(useDefaultLogo: boolean) {
		if (useDefaultLogo) {
			this.logo.clearValidators();
		} else {
			this.logo.setValidators([Validators.required]);
		}
		this.logo.updateValueAndValidity();
	}

	onSocialMediaArrayChanged() {
		this.socialMedialListInvalid = !this.socialMediaArray.valid;
	}

	ngOnDestroy() {
		if (this.useDefaultBackgroundImageChangedSubscription) {
			this.useDefaultBackgroundImageChangedSubscription.unsubscribe();
		}
		if (this.useDefaultLogoChangedSubscription) {
			this.useDefaultLogoChangedSubscription.unsubscribe();
		}
		if (this.socialMediaArrayChangedSubscription) {
			this.socialMediaArrayChangedSubscription.unsubscribe();
		}
	}

	//#endregion

	//#region Base component methods
	// overidden from CreateUpdateBaseComponent base class
	protected createFormGroup(): FormGroup<any> {
		return this.fb.group({
			name: ['', [Validators.required, Validators.maxLength(200)]],
			backgroundColour: [''],
			useDefaultBackgroundImage: [''],
			backgroundImage: [''],
			useDefaultLogo: [''],
			logo: [''],
			showSocialMediaLinks: [''],
			socialMediaList: this.fb.array([]),
			headingBackgroundColour: [''],
			headingTextColour: [''],
			borderColour: [''],
			contentAreaBackgroundColour: [''],
			roundedCorners: [''],
			controlShadow: [''],
			baseFontSize: [''],
			headingFont: [''],
			headingFontUppercase: [''],
			standardTextFont: [''],
			standardTextFontColour: [''],
			importantTextFontColour: [''],
			linkColour: [''],
			linkUnderline: [''],
			hoverColour: [''],
			hoverUnderline: [''],
			buttonFont: [''],
			buttonFontUppercase: [''],
			buttonRoundedCorners: [''],
			primaryButtonBackgroundColour: [''],
			primaryButtonTextColour: [''],
			primaryButtonBorderColour: [''],
			secondaryButtonBackgroundColour: [''],
			secondaryButtonTextColour: [''],
			secondaryButtonBorderColour: [''],
			homepageLayout: ['', [Validators.required]],
			enableSearch: [''],
			termsAndConditionsUrl: ['', [Validators.required, Validators.maxLength(200), Validators.pattern(RegexValidationStrings.WEBSITE_URL)]],
			contactUsUrl: ['', [Validators.required, Validators.pattern(RegexValidationStrings.WEBSITE_URL), Validators.maxLength(200)]],
		});
	}

	// overidden from CreateUpdateBaseComponent base class
	protected populateControls(datafrom: FormGroup<any>) {
		this.name.setValue(this.viewModel.name);
		this.backgroundColour.setValue(this.viewModel.backgroundColour);
		this.backgroundImage.setValue(this.viewModel.backgroundImage);
		this.logo.setValue(this.viewModel.logo);
		this.headingBackgroundColour.setValue(this.viewModel.headingBackgroundColour);
		this.headingTextColour.setValue(this.viewModel.headingTextColour);
		this.borderColour.setValue(this.viewModel.borderColour);
		this.contentAreaBackgroundColour.setValue(this.viewModel.contentAreaBackgroundColour);
		this.roundedCorners.setValue(this.isCreateMode ? DesignRoundedCornerOption.N8 : this.viewModel.roundedCorners);
		this.controlShadow.setValue(this.isCreateMode ? true : this.viewModel.controlShadow);
		this.baseFontSize.setValue(this.isCreateMode ? DesignBaseFontSize.Medium : this.viewModel.baseFontSize);
		this.headingFont.setValue(this.isCreateMode ? DesignFont.Arial : this.viewModel.headingFont);
		this.headingFontUppercase.setValue(this.isCreateMode ? false : this.viewModel.headingFontUppercase);
		this.standardTextFont.setValue(this.isCreateMode ? DesignFont.Arial : this.viewModel.standardTextFont);
		this.standardTextFontColour.setValue(this.viewModel.standardTextFontColour);
		this.importantTextFontColour.setValue(this.viewModel.importantTextFontColour);
		this.linkColour.setValue(this.viewModel.linkColour);
		this.linkUnderline.setValue(this.isCreateMode ? false : this.viewModel.linkUnderline);
		this.hoverColour.setValue(this.viewModel.hoverColour);
		this.hoverUnderline.setValue(this.isCreateMode ? false : this.viewModel.hoverUnderline);
		this.buttonFont.setValue(this.isCreateMode ? DesignFont.Arial : this.viewModel.buttonFont);
		this.buttonFontUppercase.setValue(this.isCreateMode ? false : this.viewModel.buttonFontUppercase);
		this.buttonRoundedCorners.setValue(this.isCreateMode ? DesignRoundedCornerOption.N4 : this.viewModel.buttonRoundedCorners);
		this.primaryButtonBackgroundColour.setValue(this.viewModel.primaryButtonBackgroundColour);
		this.primaryButtonTextColour.setValue(this.viewModel.primaryButtonTextColour);
		this.primaryButtonBorderColour.setValue(this.viewModel.primaryButtonBorderColour);
		this.secondaryButtonBackgroundColour.setValue(this.viewModel.secondaryButtonBackgroundColour);
		this.secondaryButtonTextColour.setValue(this.viewModel.secondaryButtonTextColour);
		this.secondaryButtonBorderColour.setValue(this.viewModel.secondaryButtonBorderColour);

		this.homepageLayout.setValue(this.viewModel.homepageLayout);

		this.enableSearch.setValue(this.isCreateMode ? false : this.viewModel.enableSearch);
		this.termsAndConditionsUrl.setValue(this.viewModel.termsAndConditionsUrl);
		this.contactUsUrl.setValue(this.viewModel.contactUsUrl);

		this.useDefaultBackgroundImageChangedSubscription = this.useDefaultBackgroundImage.valueChanges.subscribe((value) => this.onDefaultBackgroundImageChanged(value));
		this.useDefaultBackgroundImage.setValue(this.isCreateMode ? true : this.viewModel.useDefaultBackgroundImage);

		this.useDefaultLogoChangedSubscription = this.useDefaultLogo.valueChanges.subscribe((value) => this.onDefaultLogoChanged(value));
		this.useDefaultLogo.setValue(this.isCreateMode ? true : this.viewModel.useDefaultLogo);

		this.showSocialMediaLinks.setValue(this.isCreateMode ? false : this.viewModel.showSocialMediaLinks);
		this.dataForm.setControl('socialMediaList', SocialMediaFormListComponent.buildSocialMediaList(this.fb, this.viewModel.socialMediaList));
		this.socialMediaArrayChangedSubscription = this.socialMediaArray.valueChanges.subscribe(() => this.onSocialMediaArrayChanged());

		this.roundedCornersLookupData = this.formHelperService.createLookupData(this.viewModel.roundedCornersLookupData);
		this.baseFontSizeLookupData = this.formHelperService.createLookupData(this.viewModel.baseFontSizeLookupData);
		this.headingFontLookupData = this.formHelperService.createLookupData(this.viewModel.headingFontLookupData);
		this.standardTextFontLookupData = this.formHelperService.createLookupData(this.viewModel.standardTextFontLookupData);
		this.buttonFontLookupData = this.formHelperService.createLookupData(this.viewModel.buttonFontLookupData);
		this.buttonRoundedCornersLookupData = this.formHelperService.createLookupData(this.viewModel.buttonRoundedCornersLookupData);
		this.homepageLayoutLookupData = this.formHelperService.createLookupData(this.viewModel.homepageLayoutLookupData);
	}

	// overidden from CreateUpdateBaseComponent base class
	protected async loadData(id: number): Promise<TicketShopDesignFormModel> {
		let dataModel: TicketShopDesignFormModel;

		await this.service.getEditableItemDetail(id).then((response: TicketShopDesignFormModel) => {
			dataModel = response;
		});

		return Promise.resolve(dataModel);
	}

	// overidden from CreateUpdateBaseComponent base class
	protected async createUpdateModel() {
		var backgroundImageHasChanged = this.viewModel.backgroundImage !== this.backgroundImage.value;
		var logoHasChanged = this.viewModel.logo !== this.logo.value;

		return {
			name: this.name.value,
			backgroundColour: this.backgroundColour.value,
			useDefaultBackgroundImage: this.useDefaultBackgroundImage.value,
			backgroundImage: this.backgroundImage.value,
			backgroundImageHasChanged: backgroundImageHasChanged,
			useDefaultLogo: this.useDefaultLogo.value,
			logo: this.logo.value,
			logoHasChanged: logoHasChanged,
			showSocialMediaLinks: this.showSocialMediaLinks.value,
			socialMediaList: this.socialMediaArray.controls.map((socialMediaItem) => {
				return {
					socialPlatformId: socialMediaItem.get('socialPlatformId').value,
					name: socialMediaItem.get('name').value,
					included: socialMediaItem.get('included').value,
					url: socialMediaItem.get('url').value,
				};
			}),
			headingBackgroundColour: this.headingBackgroundColour.value,
			headingTextColour: this.headingTextColour.value,
			borderColour: this.borderColour.value,
			contentAreaBackgroundColour: this.contentAreaBackgroundColour.value,
			roundedCorners: this.roundedCorners.value,
			controlShadow: this.controlShadow.value,
			baseFontSize: this.baseFontSize.value,
			headingFont: this.headingFont.value,
			headingFontUppercase: this.headingFontUppercase.value,
			standardTextFont: this.standardTextFont.value,
			standardTextFontColour: this.standardTextFontColour.value,
			importantTextFontColour: this.importantTextFontColour.value,
			linkColour: this.linkColour.value,
			linkUnderline: this.linkUnderline.value,
			hoverColour: this.hoverColour.value,
			hoverUnderline: this.hoverUnderline.value,
			buttonFont: this.buttonFont.value,
			buttonFontUppercase: this.buttonFontUppercase.value,
			buttonRoundedCorners: this.buttonRoundedCorners.value,
			primaryButtonBackgroundColour: this.primaryButtonBackgroundColour.value,
			primaryButtonTextColour: this.primaryButtonTextColour.value,
			primaryButtonBorderColour: this.primaryButtonBorderColour.value,
			secondaryButtonBackgroundColour: this.secondaryButtonBackgroundColour.value,
			secondaryButtonTextColour: this.secondaryButtonTextColour.value,
			secondaryButtonBorderColour: this.secondaryButtonBorderColour.value,
			homepageLayout: this.homepageLayout.value,
			enableSearch: this.enableSearch.value,
			termsAndConditionsUrl: this.termsAndConditionsUrl.value,
			contactUsUrl: this.contactUsUrl.value,
			timestamp: this.viewModel ? this.viewModel.timestamp : null,
		};
	}

	protected async doUpdate(id: number, dataToUpdate: any): Promise<ApiResponseSummary<any>> {
		let updateResponse: ApiResponseSummary<any>;

		await this.service.updateItem(id, dataToUpdate).then((response: ApiResponseSummary<any>) => {
			updateResponse = response;
		});

		return Promise.resolve(updateResponse);
	}

	// overidden from CreateUpdateBaseComponent base class
	protected async doCreate(dataToUpdate: any) {
		await this.service.addNew(dataToUpdate);
	}

	async publishPreview() {
		await this.eventDetailService.publishEventPreview(this.data.itemId, this.data.eventId);
	}

	// overidden from CreateUpdateBaseComponent base class
	async save(publishPreviewAfterSave: boolean = false) {
		this.backgroundImage.updateValueAndValidity();
		this.logo.updateValueAndValidity();
		var backgroundImageHasNoErrors = this.backgroundImage.valid && (this.backgroundImageIsValid || this.backgroundImageIsValid === null || this.backgroundImageIsValid === undefined);
		var logoHasNoErrors = this.logo.valid && (this.logoIsValid || this.logoIsValid === null || this.logoIsValid === undefined);

		if (backgroundImageHasNoErrors && logoHasNoErrors) {
			if (this.formHasAsyncValidators) {
				// If the form has async validators, we need to wait for them to complete before proceeding
				this.waitForPendingFormStatusChange();
			}

			if (this.formHelperService.isFormValid(this.dataForm)) {
				const updateModel = await this.createUpdateModel();
				this.startBlockUI();
				if (this.performUpdate) {
					this.doUpdate(this.data.itemId, updateModel)
						.then(async (response: ApiResponseSummary<any>) => {
							if (response.success === true) {
								if (publishPreviewAfterSave) {
									await this.publishPreview().then(() => {
										this.asideService.close({ message: 'preview-publish-update', itemId: this.data.itemId });
									});
								} else {
									this.asideService.close(this.asideMessage);
								}
							} else {
								this.concurrencyErrorMessage = response.message;
							}
							this.stopBlockUI();
						})
						.catch((err) => {
							this.stopBlockUI();
							throw err;
						});
				} else {
					this.doCreate(updateModel)
						.then(() => {
							this.stopBlockUI();
							this.asideService.close(this.asideMessage);
						})
						.catch((err) => {
							this.stopBlockUI();
							throw err;
						});
				}
			} else {
				logValidationErrorsForDevAndTest(this.dataForm);
			}
		}
	}

	//#endregion

	//#region collapsible region validators
	getBackgroundHasError(): boolean {
		return (this.backgroundImage.touched && this.backgroundImage.invalid) || this.backgroundImageIsValid === false;
	}
	getHeaderHasError(): boolean {
		return this.getHeaderLogoHasError() || (this.socialMedialListInvalid && this.socialMediaArray.touched);
	}
	getHomePageHasError(): boolean {
		return this.homepageLayout.touched && this.homepageLayout.invalid;
	}
	getFooterHasError(): boolean {
		return (this.termsAndConditionsUrl.touched && this.termsAndConditionsUrl.invalid) || (this.contactUsUrl.touched && this.contactUsUrl.invalid);
	}
	//#endregion

	//#region Image selector methods

	setImageValidationMessage(invalidErrorType: ImageUploadErrorType, isBackgroundImage: boolean) {
		switch (invalidErrorType) {
			case ImageUploadErrorType.InvalidMimeType:
				if (isBackgroundImage) {
					this.setImageIsValid(false, isBackgroundImage);
					this.backgroundImageValidationMessage = 'Background Image must be a jpeg or png file type';
				} else {
					this.logoValidationMessage = 'Header Logo must be a jpeg or png file type';
				}
				break;
			case ImageUploadErrorType.FileTooLarge:
				this.setImageIsValid(false, isBackgroundImage);
				if (isBackgroundImage) {
					this.setImageIsValid(false, isBackgroundImage);
					this.backgroundImageValidationMessage = 'Background Image must be less than 10MB in size';
				} else {
					this.logoValidationMessage = 'Header Logo must be less than 10MB in size';
				}
				break;
			default:
				this.setImageIsValid(true, true);
				this.setImageIsValid(true, true);
				this.backgroundImageValidationMessage = '';
				this.logoValidationMessage = '';
				break;
		}
	}

	setImageIsValid(isValid: boolean, isBackgroundImage: boolean) {
		if (isBackgroundImage) {
			this.backgroundImageIsValid = isValid;
		} else {
			this.logoIsValid = isValid;
		}
	}

	setImageDeleted(isBackgroundImage: boolean) {
		if (isBackgroundImage) {
			this.backgroundImageIsValid = true;
			this.backgroundImageValidationMessage = '';
		} else {
			this.logoIsValid = true;
			this.logoValidationMessage = '';
		}
	}

	backgroundImageErrorMessage(): string {
		if (this.getBackgroundHasError()) {
			if (this.backgroundImage.errors?.required) {
				return 'Background Image is required';
			} else {
				return this.backgroundImage.errors?.upload ?? this.backgroundImageValidationMessage;
			}
		}
		return '';
	}

	getHeaderLogoHasError(): boolean {
		return this.logo.touched && (this.logo.invalid || this.logoIsValid === false);
	}

	headerLogoErrorMessage(): string {
		if (this.getHeaderLogoHasError()) {
			if (this.logo.errors?.required) {
				return 'Header Logo is required';
			} else {
				return this.logo.errors?.upload ?? this.logoValidationMessage;
			}
		}
		return '';
	}
	//#endregion

	//#region Layout picker methods
	getHomePageLayoutImageName(id: DesignHomePageLayout) {
		switch (id) {
			case DesignHomePageLayout.ListImage:
				return 'homepage-layout-list';
			case DesignHomePageLayout.Column1:
				return 'homepage-layout-columns-1';
			case DesignHomePageLayout.Column2:
				return 'homepage-layout-columns-2';
			case DesignHomePageLayout.Column3:
				return 'homepage-layout-columns-3';
			case DesignHomePageLayout.Column4:
				return 'homepage-layout-columns-4';
			default:
				break;
		}
	}
	getHomePageLayoutImageTooltip(id: DesignHomePageLayout) {
		switch (id) {
			case DesignHomePageLayout.ListImage:
				return 'List';
			case DesignHomePageLayout.Column1:
				return 'Panels: One column';
			case DesignHomePageLayout.Column2:
				return 'Panels: Two columns';
			case DesignHomePageLayout.Column3:
				return 'Panels: Three columns';
			case DesignHomePageLayout.Column4:
				return 'Panels: Four columns';
			default:
				break;
		}
	}
	onHomePageLayoutStyleSelected(lookupId: number): void {
		this.homepageLayout.setValue(lookupId);
		this.homepageLayout.markAsDirty();
	}
	//#endregion
	showForm: boolean = true;
	showHide() {
		this.showForm = !this.showForm;
	}
	showHideTitle(): string {
		return this.showForm ? 'Hide Form' : 'Show Form';
	}
}
